import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, Download, Folder } from 'lucide-react';

const CAT_KEYS = ['contract', 'drawing', 'claim', 'letter', 'minutes', 'permit', 'report', 'other'];

const Documents = () => {
  const { t } = useTranslation();
  const CATEGORIES = CAT_KEYS.map((v) => ({ value: v, label: t(`documents.categories.${v}`) }));
  const [list, setList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState({ category: '', project_id: '' });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'other', project_id: '', tags: '', notes: '' });
  const [file, setFile] = useState(null);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.project_id) params.append('project_id', filter.project_id);
      const [a, b] = await Promise.all([
        axiosInstance.get(`/documents?${params}`),
        axiosInstance.get('/projects'),
      ]);
      setList(a.data || []); setProjects(b.data || []);
    } catch { toast.error('Failed to load'); }
  };
  useEffect(() => { load(); }, [filter.category, filter.project_id]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Choose a file');
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
    try {
      await axiosInstance.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded'); setShow(false); setFile(null); setForm({ title: '', category: 'other', project_id: '', tags: '', notes: '' });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete document?')) return;
    try { await axiosInstance.delete(`/documents/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Folder size={24} /> {t('documents.title')}</h1>
        <button data-testid="upload-doc-btn" onClick={() => setShow(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold">
          <Upload size={18} /> {t('documents.upload')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow p-4 flex gap-4">
        <select value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })} className="border rounded px-3 py-2 text-sm">
          <option value="">{t('documents.all_categories')}</option>
          {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select value={filter.project_id} onChange={(e) => setFilter({ ...filter, project_id: e.target.value })} className="border rounded px-3 py-2 text-sm">
          <option value="">{t('documents.all_projects')}</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((d) => (
          <div key={d.id} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <FileText className="text-purple-600" size={20} />
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{CATEGORIES.find((c) => c.value === d.category)?.label || d.category}</span>
              </div>
              <div className="flex gap-1">
                <a href={`${API}/uploads/${d.file_path}`} target="_blank" rel="noopener noreferrer" className="p-1 text-blue-600 hover:bg-blue-50 rounded"><Download size={14} /></a>
                <button onClick={() => del(d.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
              </div>
            </div>
            <h3 className="font-semibold text-sm mb-1 truncate">{d.title}</h3>
            <p className="text-xs text-gray-500 truncate">{d.file_name}</p>
            {d.project_name && <p className="text-xs text-gray-600 mt-1">📁 {d.project_name}</p>}
            <p className="text-xs text-gray-400 mt-2">{(d.file_size / 1024).toFixed(0)} KB · {new Date(d.created_at).toLocaleDateString()}</p>
            {d.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {d.tags.map((t, i) => <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">{t}</span>)}
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center text-gray-400 py-10">{t('documents.empty')}</div>}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <form onSubmit={upload} className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b"><h2 className="text-xl font-bold">{t('documents.upload')}</h2></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-xs text-gray-600 mb-1 block">File *</label>
                <input type="file" required onChange={(e) => setFile(e.target.files[0])} className="w-full border rounded px-3 py-2 text-sm" data-testid="doc-file" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Title *</label>
                <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" data-testid="doc-title" />
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Category *</label>
                <select required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Project</label>
                <select value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">— None —</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Tags (comma-separated)</label>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="w-full border rounded px-3 py-2 text-sm" placeholder="contract, 2026, phase-1" />
              </div>
            </div>
            <div className="p-6 border-t flex gap-2 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 rounded-lg border text-sm">{t('common.cancel')}</button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm" data-testid="save-doc-btn">{t('documents.upload')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Documents;
